import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';
import { SLASH_COMMANDS } from './slashCommands';

interface Options {
  plainText: string;
  /** Replaces the editor content with the completed command. */
  onComplete: (_text: string) => void;
}

// Keyboard-driven autocomplete state: the first match is pre-selected,
// arrows move the highlight, Enter/Tab complete it, Escape dismisses the
// popover until the query changes. Index resets whenever the query does.
export const useSlashCommands = ({ plainText, onComplete }: Options) => {
  const query =
    plainText.startsWith('/') && !plainText.includes(' ')
      ? plainText.toLowerCase()
      : null;
  const matches = useMemo(
    () =>
      query !== null
        ? SLASH_COMMANDS.filter((c) => c.cmd.startsWith(query))
        : [],
    [query]
  );
  // Highlight + dismissal are keyed to the query so a new query resets both.
  const [nav, setNav] = useState({ query, index: 0, dismissed: false });
  if (nav.query !== query) setNav({ query, index: 0, dismissed: false });

  const setIndex = useCallback(
    (index: number) => setNav((n) => ({ ...n, index })),
    []
  );

  const open = !nav.dismissed && query !== null && matches.length > 0;
  const activeIndex = Math.min(nav.index, Math.max(matches.length - 1, 0));

  const apply = useCallback(
    (cmd: string) => {
      onComplete(cmd);
    },
    [onComplete]
  );

  // Arrows move the highlight, Tab completes, Escape dismisses. Returns true
  // when the popover consumed the key.
  const handleNavKey = useCallback(
    (event: KeyboardEvent<HTMLElement>): boolean => {
      if (!open) return false;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setNav((n) => ({ ...n, index: (n.index + 1) % matches.length }));
          return true;
        case 'ArrowUp':
          event.preventDefault();
          setNav((n) => ({
            ...n,
            index: (n.index - 1 + matches.length) % matches.length,
          }));
          return true;
        case 'Tab':
          event.preventDefault();
          apply(matches[activeIndex].cmd);
          return true;
        case 'Escape':
          event.preventDefault();
          setNav((n) => ({ ...n, dismissed: true }));
          return true;
        default:
          return false;
      }
    },
    [activeIndex, apply, matches, open]
  );

  // Enter on a PARTIAL command completes the highlighted match instead of
  // sending the fragment — "/mar" + Enter once went out as a real message and
  // became a bogus search. A fully typed command still sends.
  const completeIfPartial = useCallback((): boolean => {
    if (!open || matches.some((c) => c.cmd === query)) return false;
    apply(matches[activeIndex].cmd);
    return true;
  }, [activeIndex, apply, matches, open, query]);

  return {
    open,
    matches,
    activeIndex,
    setIndex,
    apply,
    handleNavKey,
    completeIfPartial,
  };
};
